import { useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useLocationStore } from '@/store/location';
import { subscribeToInventory, unsubscribe } from '@/lib/realtime';
import { useAuth } from '@/hooks/useAuth';

export interface InventoryItem {
  id: string;
  product_id: string;
  location_id: string;
  quantity_on_hand: number;
  quantity_reserved: number;
  quantity_boh: number; // Back of House stock
  quantity_foh: number; // Front of House stock
  reorder_point: number | null;
  reorder_quantity: number | null;
  last_counted_at: string | null;
  created_at: string;
  updated_at: string;
  location?: {
    id: string;
    name: string;
  } | null;
  product?: {
    id: string;
    name: string;
    sku: string | null;
    barcode: string | null;
    price: number;
    cost: number | null;
    image_url: string | null;
    is_active: boolean;
    is_available: boolean;
    is_sellable: boolean;
    category_id: string | null;
    product_type?: string | null;
    tax_rate?: number | null;
    description?: string | null;
    category?: {
      id: string;
      name: string;
    } | null;
  };
}

export interface InventoryTransaction {
  id: string;
  inventory_item_id: string;
  user_id: string | null;
  transaction_type: 'received' | 'sold' | 'adjusted' | 'transferred' | 'returned' | 'waste' | 'transfer_boh_to_foh' | 'transfer_foh_to_boh' | 'consumed';
  quantity_change: number;
  quantity_before: number | null;
  quantity_after: number | null;
  reference_type: string | null;
  reference_id: string | null;
  notes: string | null;
  created_at: string;
  user?: {
    first_name: string | null;
    last_name: string | null;
  } | null;
}

export interface InventoryTransfer {
  id: string;
  inventory_item_id: string;
  location_id: string;
  user_id: string;
  transfer_type: 'boh_to_foh' | 'foh_to_boh';
  quantity: number;
  notes: string | null;
  created_at: string;
  user?: {
    first_name: string | null;
    last_name: string | null;
  } | null;
  inventory_item?: {
    product?: {
      id: string;
      name: string;
    };
  };
}

export interface StockOverrideLog {
  id: string;
  location_id: string;
  product_id: string;
  order_id: string | null;
  cashier_id: string;
  manager_id: string;
  override_reason: string | null;
  foh_quantity_at_time: number;
  quantity_sold: number;
  created_at: string;
  product?: {
    id: string;
    name: string;
  };
  cashier?: {
    first_name: string | null;
    last_name: string | null;
  };
  manager?: {
    first_name: string | null;
    last_name: string | null;
  };
}

export interface TransferInventoryInput {
  inventory_item_id: string;
  transfer_type: 'boh_to_foh' | 'foh_to_boh';
  quantity: number;
  notes?: string;
}

export interface LogStockOverrideInput {
  product_id: string;
  order_id?: string;
  cashier_id: string;
  manager_id: string;
  override_reason?: string;
  foh_quantity_at_time: number;
  quantity_sold: number;
}

export interface CreateInventoryInput {
  product_id: string;
  location_id: string;
  quantity_on_hand?: number;
  quantity_boh?: number; // Initial BOH stock
  quantity_foh?: number; // Initial FOH stock
  reorder_point?: number | null;
  reorder_quantity?: number | null;
}

export interface AdjustInventoryInput {
  inventory_item_id: string;
  quantity_change: number;
  transaction_type: 'received' | 'received_boh' | 'adjusted' | 'transferred' | 'returned' | 'waste';
  notes?: string;
}

export interface UseInventoryOptions {
  /**
   * When true, this hook will respect the global `locationScope === 'all'` setting
   * and fetch inventory across the organization. Defaults to false to avoid
   * accidentally impacting location-specific flows (e.g. POS stock checks).
   */
  allowAllLocations?: boolean;
}

/**
 * Get the effective FOH quantity for an inventory item.
 * Handles legacy items where quantity_foh=0 and quantity_boh=0
 * but quantity_on_hand > 0 (items created before the BOH/FOH split).
 * In that case the entire on-hand quantity is treated as available for sale.
 */
export function getEffectiveFOH(item: Pick<InventoryItem, 'quantity_foh' | 'quantity_boh' | 'quantity_on_hand'>): number {
  const foh = item.quantity_foh ?? 0;
  const boh = item.quantity_boh ?? 0;
  const total = item.quantity_on_hand ?? 0;

  // Legacy item: both FOH and BOH are 0 but total has stock.
  // This happens for items created before the BOH/FOH split was added.
  // Treat the total quantity as FOH (available for sale).
  if (foh === 0 && boh === 0 && total > 0) {
    return total;
  }

  return foh;
}

export function useInventory(options: UseInventoryOptions = {}) {
  const queryClient = useQueryClient();
  const { organization } = useAuth();
  const { currentLocation, locationScope } = useLocationStore();
  const locationId = currentLocation?.id;
  const organizationIdForAll = organization?.id || currentLocation?.organization_id || null;
  const viewingAllLocations = !!options.allowAllLocations && locationScope === 'all';

  // Fetch inventory items with product details
  const {
    data: inventoryItems,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['inventory', organizationIdForAll, viewingAllLocations ? 'all' : locationId],
    queryFn: async () => {
      if (viewingAllLocations) {
        if (!organizationIdForAll) return [];

        const { data, error } = await supabase
          .from('inventory_items')
          .select(`
            *,
            location:locations!inner(id, name, organization_id),
            product:products(
              id,
              name,
              sku,
              barcode,
              price,
              cost,
              image_url,
              is_active,
              is_available,
              is_sellable,
              category_id,
              product_type,
              tax_rate,
              description,
              category:categories(id, name)
            )
          `)
          .eq('location.organization_id', organizationIdForAll)
          .order('updated_at', { ascending: false });

        if (error) throw error;
        return data as InventoryItem[];
      }

      if (!locationId) return [];

      const { data, error } = await supabase
        .from('inventory_items')
        .select(`
          *,
          product:products(
            id,
            name,
            sku,
            barcode,
            price,
            cost,
            image_url,
            is_active,
            is_available,
            is_sellable,
            category_id,
            product_type,
            tax_rate,
            description,
            category:categories(id, name)
          )
        `)
        .eq('location_id', locationId)
        .order('updated_at', { ascending: false });

      if (error) throw error;
      return data as InventoryItem[];
    },
    enabled: viewingAllLocations ? !!organizationIdForAll : !!locationId,
  });

  // Subscribe to real-time inventory updates
  useEffect(() => {
    if (viewingAllLocations || !locationId) return;

    const channel = subscribeToInventory(locationId, () => {
      // Invalidate and refetch inventory data when changes occur
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    });

    return () => {
      unsubscribe(channel);
    };
  }, [locationId, queryClient, viewingAllLocations]);

  // Fetch composite product virtual inventory (how many can be made from components)
  const { data: compositeVirtualInventory } = useQuery({
    queryKey: ['composite-virtual-inventory', locationId],
    queryFn: async () => {
      if (!locationId) return [];
      const { data, error } = await supabase
        .from('composite_product_virtual_inventory')
        .select('product_id, virtual_quantity')
        .eq('location_id', locationId)
        .is('variant_id', null); // Get product-level totals, not per-variant
      if (error) {
        console.error('Error fetching composite virtual inventory:', error);
        return [];
      }
      return data as { product_id: string; virtual_quantity: number }[];
    },
    enabled: !!locationId && !viewingAllLocations,
  });

  // Build a lookup map: product_id -> virtual quantity for composite products
  const compositeStockMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of compositeVirtualInventory || []) {
      map.set(item.product_id, item.virtual_quantity ?? 0);
    }
    return map;
  }, [compositeVirtualInventory]);

  // Fetch inventory transactions for a specific item
  const fetchTransactions = async (inventoryItemId: string) => {
    const { data, error } = await supabase
      .from('inventory_transactions')
      .select(`
        *,
        user:profiles(first_name, last_name)
      `)
      .eq('inventory_item_id', inventoryItemId)
      .order('created_at', { ascending: false })
      .limit(50);

    if (error) throw error;
    return data as InventoryTransaction[];
  };

  // Get low stock items (has FOH stock but at or below reorder point)
  const lowStockItems = (inventoryItems || []).filter(
    (item) => item.reorder_point && 
              getEffectiveFOH(item) > 0 && 
              getEffectiveFOH(item) <= item.reorder_point
  );

  // Get out of stock items (no FOH stock available for sale)
  const outOfStockItems = (inventoryItems || []).filter(
    (item) => getEffectiveFOH(item) <= 0
  );

  // Get items with BOH stock available (can be transferred to FOH)
  const itemsWithBOHStock = (inventoryItems || []).filter(
    (item) => (item.quantity_boh ?? 0) > 0
  );

  // Create inventory item for a product
  const createInventoryItem = useMutation({
    mutationFn: async (input: CreateInventoryInput) => {
      // First, enable track_inventory on the product so the trigger will deduct on sales
      const { error: productUpdateError } = await supabase
        .from('products')
        .update({ track_inventory: true })
        .eq('id', input.product_id);

      if (productUpdateError) {
        console.error('Failed to enable track_inventory on product:', productUpdateError);
        // Continue anyway - the inventory item should still be created
      }

      // Calculate quantities - if BOH/FOH not specified, put all in FOH (available for sale)
      const quantityBoh = input.quantity_boh ?? 0;
      const quantityFoh = input.quantity_foh ?? (input.quantity_on_hand ?? 0);
      const totalQuantity = quantityBoh + quantityFoh;

      // Create the inventory item
      const { data, error } = await supabase
        .from('inventory_items')
        .insert({
          product_id: input.product_id,
          location_id: input.location_id,
          quantity_on_hand: totalQuantity,
          quantity_boh: quantityBoh,
          quantity_foh: quantityFoh,
          reorder_point: input.reorder_point,
          reorder_quantity: input.reorder_quantity,
        })
        .select(`
          *,
          product:products(
            id,
            name,
            sku,
            barcode,
            price,
            cost,
            image_url,
            category_id,
            category:categories(id, name)
          )
        `)
        .single();

      if (error) throw error;
      return data as InventoryItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['products'] }); // Also refresh products since track_inventory changed
    },
  });

  // Update inventory item settings
  const updateInventoryItem = useMutation({
    mutationFn: async ({ 
      id, 
      updates 
    }: { 
      id: string; 
      updates: Partial<Pick<InventoryItem, 'reorder_point' | 'reorder_quantity'>> 
    }) => {
      const { data, error } = await supabase
        .from('inventory_items')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });

  // Adjust inventory (add/remove stock)
  const adjustInventory = useMutation({
    mutationFn: async (input: AdjustInventoryInput) => {
      if (!Number.isFinite(input.quantity_change) || input.quantity_change === 0) {
        throw new Error('Quantity change must be a non-zero number');
      }

      const { data, error } = await supabase.rpc('apply_product_inventory_adjustment', {
        p_inventory_item_id: input.inventory_item_id,
        p_quantity_change: input.quantity_change,
        p_transaction_type: input.transaction_type,
        p_notes: input.notes || null,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });

  // Perform stock count (update to exact quantity)
  const performStockCount = useMutation({
    mutationFn: async ({ 
      inventoryItemId, 
      newQuantity, 
      notes 
    }: { 
      inventoryItemId: string; 
      newQuantity: number; 
      notes?: string;
    }) => {
      if (!Number.isFinite(newQuantity) || newQuantity < 0) {
        throw new Error('Stock count must be a non-negative number');
      }

      const { data, error } = await supabase.rpc('record_product_inventory_total_count', {
        p_inventory_item_id: inventoryItemId,
        p_quantity_on_hand: newQuantity,
        p_notes: notes || null,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });

  // Transfer inventory between BOH and FOH
  const transferInventory = useMutation({
    mutationFn: async (input: TransferInventoryInput) => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user?.id) throw new Error('User not authenticated');

      // Call the database function for atomic transfer
      const { data, error } = await supabase.rpc('transfer_inventory_boh_foh', {
        p_inventory_item_id: input.inventory_item_id,
        p_user_id: user.user.id,
        p_transfer_type: input.transfer_type,
        p_quantity: input.quantity,
        p_notes: input.notes || null,
      });

      if (error) throw error;
      
      // The function returns a table with success, message, new_boh, new_foh
      const result = data?.[0];
      if (!result?.success) {
        throw new Error(result?.message || 'Transfer failed');
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-transfers', locationId] });
    },
  });

  // Migrate legacy total inventory to BOH
  const migrateToBOH = useMutation({
    mutationFn: async (input: { inventory_item_id: string; notes?: string }) => {
      const { data, error } = await supabase.rpc('migrate_total_to_boh', {
        p_inventory_item_id: input.inventory_item_id,
        p_notes: input.notes || 'Migrated legacy inventory to BOH',
      });

      if (error) throw error;

      const result = data?.[0];
      if (!result?.success) {
        throw new Error(result?.message || 'Failed to migrate inventory to BOH');
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });

  // Log a stock override (when manager approves selling out-of-stock item)
  const logStockOverride = useMutation({
    mutationFn: async (input: LogStockOverrideInput) => {
      if (!locationId) throw new Error('No location selected');

      const { data, error } = await supabase
        .from('stock_override_logs')
        .insert({
          location_id: locationId,
          product_id: input.product_id,
          order_id: input.order_id || null,
          cashier_id: input.cashier_id,
          manager_id: input.manager_id,
          override_reason: input.override_reason || null,
          foh_quantity_at_time: input.foh_quantity_at_time,
          quantity_sold: input.quantity_sold,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-override-logs', locationId] });
    },
  });

  // Fetch inventory transfers for the location
  const fetchTransfers = async (inventoryItemId?: string) => {
    if (!locationId) return [];

    let query = supabase
      .from('inventory_transfers')
      .select(`
        *,
        user:profiles(first_name, last_name),
        inventory_item:inventory_items(
          product:products(id, name)
        )
      `)
      .eq('location_id', locationId)
      .order('created_at', { ascending: false })
      .limit(100);

    if (inventoryItemId) {
      query = query.eq('inventory_item_id', inventoryItemId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data as InventoryTransfer[];
  };

  // Fetch stock override logs for the location
  const fetchStockOverrideLogs = async (dateFrom?: string, dateTo?: string) => {
    if (!locationId) return [];

    let query = supabase
      .from('stock_override_logs')
      .select(`
        *,
        product:products(id, name),
        cashier:profiles!stock_override_logs_cashier_id_fkey(first_name, last_name),
        manager:profiles!stock_override_logs_manager_id_fkey(first_name, last_name)
      `)
      .eq('location_id', locationId)
      .order('created_at', { ascending: false });

    if (dateFrom) {
      query = query.gte('created_at', dateFrom);
    }
    if (dateTo) {
      query = query.lte('created_at', dateTo);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data as StockOverrideLog[];
  };

  // Get FOH stock for a specific product (used by POS to check availability)
  // For composite products, uses virtual inventory (component-based availability)
  const getFOHStock = (productId: string): number => {
    const item = (inventoryItems || []).find(i => i.product_id === productId);

    // Check if this is a composite product with virtual inventory
    const compositeStock = compositeStockMap.get(productId);
    if (compositeStock !== undefined) {
      // Composite product: use virtual inventory (how many can be made from components)
      // If the composite also has direct inventory, add that too
      const directStock = item ? getEffectiveFOH(item) : 0;
      return compositeStock + directStock;
    }

    if (!item) return 0;
    return getEffectiveFOH(item);
  };

  // Fetch inventory across all locations for a specific product
  const fetchMultiLocationStock = async (productId: string): Promise<{
    location_id: string;
    location_name: string;
    quantity_on_hand: number;
    quantity_boh: number;
    quantity_foh: number;
    inbound_quantity: number;
    items_sold_30_days: number;
  }[]> => {
    const { data, error } = await supabase
      .from('inventory_items')
      .select(`
        id,
        location_id,
        quantity_on_hand,
        quantity_boh,
        quantity_foh,
        location:locations(id, name)
      `)
      .eq('product_id', productId);

    if (error) throw error;

    // Get inbound quantities from pending goods transfers
    const { data: inboundData, error: inboundError } = await supabase
      .from('goods_transfers')
      .select('to_location_id, items:goods_transfer_items(product_id, quantity_sent, quantity_received)')
      .in('status', ['in_transit', 'partially_received']);

    if (inboundError) throw inboundError;

    // Get items sold in last 30 days
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const inventoryItemIds = (data || []).map((item: any) => item.id);
    let soldByInventoryItem = new Map<string, number>();
    if (inventoryItemIds.length > 0) {
      const { data: salesData } = await supabase
        .from('inventory_transactions')
        .select('inventory_item_id, quantity_change')
        .eq('transaction_type', 'sold')
        .in('inventory_item_id', inventoryItemIds)
        .gte('created_at', thirtyDaysAgo.toISOString());

      soldByInventoryItem = new Map<string, number>();
      for (const txn of salesData || []) {
        const key = txn.inventory_item_id;
        const next = (soldByInventoryItem.get(key) || 0) + Math.abs(txn.quantity_change || 0);
        soldByInventoryItem.set(key, next);
      }
    }

    // Build result
    return (data || []).map((item: any) => {
      const inbound = inboundData?.reduce((sum: number, transfer: any) => {
        if (transfer.to_location_id === item.location_id) {
          const itemQty = transfer.items?.reduce((s: number, i: any) => {
            if (i.product_id !== productId) return s;
            return s + Math.max(0, (i.quantity_sent || 0) - (i.quantity_received || 0));
          }, 0) || 0;
          return sum + itemQty;
        }
        return sum;
      }, 0) || 0;

      const soldQty = soldByInventoryItem.get(item.id) || 0;

      return {
        location_id: item.location_id,
        location_name: item.location?.name || 'Unknown',
        quantity_on_hand: item.quantity_on_hand || 0,
        quantity_boh: item.quantity_boh || 0,
        quantity_foh: item.quantity_foh || item.quantity_on_hand || 0,
        inbound_quantity: inbound,
        items_sold_30_days: soldQty,
      };
    });
  };

  // Check if product is out of stock in FOH
  const isOutOfStockFOH = (productId: string): boolean => {
    return getFOHStock(productId) <= 0;
  };

  // Get in-transit quantity for a product at current location
  const getInTransitQuantity = async (productId: string): Promise<number> => {
    if (!locationId) return 0;

    const { data, error } = await supabase
      .from('goods_transfer_items')
      .select(`
        quantity_sent,
        quantity_received,
        transfer:goods_transfers!inner(status, to_location_id)
      `)
      .eq('product_id', productId)
      .eq('transfer.to_location_id', locationId)
      .in('transfer.status', ['in_transit', 'partially_received']);

    if (error) {
      console.error('Error fetching in-transit quantity:', error);
      return 0;
    }

    return (data || []).reduce((sum, item) => {
      const inTransit = (item.quantity_sent || 0) - (item.quantity_received || 0);
      return sum + Math.max(0, inTransit);
    }, 0);
  };

  // Get on-order quantity for a product at current location
  const getOnOrderQuantity = async (productId: string): Promise<number> => {
    if (!locationId) return 0;

    const { data, error } = await supabase
      .from('purchase_order_items')
      .select(`
        quantity_ordered,
        quantity_received,
        purchase_order:purchase_orders!inner(status, location_id)
      `)
      .eq('product_id', productId)
      .eq('purchase_order.location_id', locationId)
      .in('purchase_order.status', ['draft', 'placed', 'delivered', 'partial']);

    if (error) {
      console.error('Error fetching on-order quantity:', error);
      return 0;
    }

    return (data || []).reduce((sum, item) => {
      const onOrder = (item.quantity_ordered || 0) - (item.quantity_received || 0);
      return sum + Math.max(0, onOrder);
    }, 0);
  };

  // Calculate need for an inventory item
  const calculateNeed = (item: InventoryItem): number => {
    const effectiveQOH = Math.max(0, getEffectiveFOH(item));
    const desiredLevel = item.reorder_quantity || (item.reorder_point ? item.reorder_point * 2 : 0);
    
    // For synchronous calculation without in-transit/on-order
    // Use the async version with getInTransitQuantity and getOnOrderQuantity for accurate Need
    const need = desiredLevel - effectiveQOH;
    return Math.max(0, need);
  };

  // Calculate need with all factors (async)
  const calculateNeedAsync = async (item: InventoryItem): Promise<{
    need: number;
    inTransit: number;
    onOrder: number;
    effectiveQOH: number;
    desiredLevel: number;
  }> => {
    const effectiveQOH = Math.max(0, getEffectiveFOH(item));
    const desiredLevel = item.reorder_quantity || (item.reorder_point ? item.reorder_point * 2 : 0);
    
    const inTransit = await getInTransitQuantity(item.product_id);
    const onOrder = await getOnOrderQuantity(item.product_id);
    
    const need = desiredLevel - (effectiveQOH + inTransit + onOrder);
    
    return {
      need: Math.max(0, need),
      inTransit,
      onOrder,
      effectiveQOH,
      desiredLevel,
    };
  };

  // Get margin for an inventory item
  const getMargin = (item: InventoryItem): { marginDollars: number; marginPercent: number } => {
    const cost = item.product?.cost || 0;
    const price = item.product?.price || 0;
    
    const marginDollars = price - cost;
    const marginPercent = price > 0 ? ((price - cost) / price) * 100 : 0;
    
    return { marginDollars, marginPercent };
  };

  // Get items with negative inventory
  const negativeStockItems = (inventoryItems || []).filter(
    (item) => getEffectiveFOH(item) < 0
  );

  return {
    inventoryItems: inventoryItems ?? [],
    isLoading,
    error,
    refetch,
    lowStockItems,
    outOfStockItems,
    itemsWithBOHStock,
    negativeStockItems,
    fetchTransactions,
    createInventoryItem,
    updateInventoryItem,
    adjustInventory,
    performStockCount,
    // BOH/FOH transfers
    transferInventory,
    fetchTransfers,
    // Legacy migration
    migrateToBOH,
    // Stock overrides
    logStockOverride,
    fetchStockOverrideLogs,
    // FOH stock checks (includes composite virtual inventory)
    getFOHStock,
    isOutOfStockFOH,
    compositeStockMap,
    // Multi-location
    fetchMultiLocationStock,
    // Reorder calculations
    getInTransitQuantity,
    getOnOrderQuantity,
    calculateNeed,
    calculateNeedAsync,
    // Margin calculations
    getMargin,
    locationId,
    viewingAllLocations,
  };
}

